import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { PackagePlus, Plus, Save, Trash2, X, Copy, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString("it-IT");

export default function Importazioni() {
  const { user } = useAuth();
  const isOwner = user?.role === "Proprietario";
  const [items, setItems] = useState([]);
  const [qty, setQty] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [newItem, setNewItem] = useState({ label: "", price: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/import-items");
      setItems(data);
      setQty((q) => Object.fromEntries(data.map((i) => [i.id, q[i.id] || 0])));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento voci import");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isOwner) load(); else setLoading(false); }, [isOwner]);

  const total = useMemo(
    () => items.reduce((sum, i) => sum + (parseInt(qty[i.id] || 0, 10) || 0) * i.price, 0),
    [items, qty]
  );

  const addItem = async () => {
    const price = parseFloat(newItem.price);
    if (!newItem.label.trim()) { toast.error("Inserisci il nome della voce"); return; }
    if (Number.isNaN(price)) { toast.error("Inserisci un prezzo valido"); return; }
    setBusy(true);
    try {
      await api.post("/import-items", { label: newItem.label.trim(), price });
      setNewItem({ label: "", price: "" });
      toast.success("Voce aggiunta");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    const price = parseFloat(editing.price);
    if (!editing.label.trim()) { toast.error("Inserisci il nome della voce"); return; }
    if (Number.isNaN(price)) { toast.error("Inserisci un prezzo valido"); return; }
    setBusy(true);
    try {
      await api.patch(`/import-items/${editing.id}`, { label: editing.label.trim(), price });
      setEditing(null);
      toast.success("Voce aggiornata");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Eliminare la voce "${item.label}"?`)) return;
    try {
      await api.delete(`/import-items/${item.id}`);
      toast.success("Voce eliminata");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const receiptText = useMemo(() => {
    const rows = items
      .map((i) => ({ label: i.label, q: parseInt(qty[i.id] || 0, 10) || 0 }))
      .filter((r) => r.q > 0);
    if (rows.length === 0) return "";
    const lines = rows.map((r) => `${r.label}: x${r.q}`);
    lines.push("");
    lines.push(`Totale: ${fmt(total)}$`);
    return lines.join("\n");
  }, [items, qty, total]);

  const copyTotal = async () => {
    if (total <= 0) { toast.error("Inserisci almeno una quantità"); return; }
    try {
      await navigator.clipboard.writeText(receiptText);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = receiptText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Lista import copiata negli appunti");
  };

  if (!isOwner) {
    return (
      <div className="w-full">
        <PageHeader icon={PackagePlus} title="Import" subtitle="Calcolo prezzi componenti import." />
        <div className="ot-panel p-6 text-slate-400 text-sm" data-testid="import-no-access">
          Solo il Proprietario può accedere al calcolo import.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <PageHeader
        icon={PackagePlus}
        title="Import"
        subtitle="Calcolo prezzi dei componenti import."
        right={
          <div className="ot-label flex items-center gap-2">
            <span className="w-2 h-2 bg-emerald-400 rounded-full" />
            {items.length} VOCI
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 ot-panel-elev p-4 ot-fade-up">
          <h2 className="ot-heading text-lg font-bold text-slate-100 mb-3">Calcolo Import</h2>
          {loading ? (
            <div className="text-slate-500 text-sm">Caricamento...</div>
          ) : (
            <div className="space-y-2" data-testid="import-items">
              {items.map((i) => {
                const isEdit = editing?.id === i.id;
                const q = parseInt(qty[i.id] || 0, 10) || 0;
                return (
                  <div key={i.id} className="ot-panel p-3" data-testid={`import-row-${i.id}`}>
                    {isEdit ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="ot-label block mb-1">NOME VOCE</label>
                            <input data-testid="edit-import-label" className="ot-input" value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
                          </div>
                          <div>
                            <label className="ot-label block mb-1">PREZZO ($)</label>
                            <input data-testid="edit-import-price" type="number" className="ot-input ot-mono" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button data-testid="save-import-btn" disabled={busy} onClick={saveEdit} className="ot-btn-primary flex items-center gap-2">
                            <Save className="w-4 h-4" /> Salva
                          </button>
                          <button data-testid="cancel-import-btn" onClick={() => setEditing(null)} className="ot-btn-ghost flex items-center gap-2">
                            <X className="w-4 h-4" /> Annulla
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                        <div className="flex-1 min-w-[45%]">
                          <div className="text-slate-200 text-sm">{i.label}</div>
                          <div className="ot-mono text-[0.65rem] text-blue-400">${fmt(i.price)}</div>
                        </div>
                        <div className="w-20 shrink-0">
                          <input
                            data-testid={`import-qty-${i.id}`}
                            type="number"
                            min="0"
                            className="ot-input ot-mono !py-1.5 !text-sm"
                            value={qty[i.id] ?? 0}
                            onChange={(e) => setQty({ ...qty, [i.id]: e.target.value })}
                          />
                        </div>
                        <div className="ot-mono text-sm font-bold text-amber-400 w-24 text-right shrink-0" data-testid={`import-subtotal-${i.id}`}>
                          ${fmt(q * i.price)}
                        </div>
                        <button
                          onClick={() => setEditing({ id: i.id, label: i.label, price: String(i.price) })}
                          data-testid={`edit-import-${i.id}`}
                          className="px-2 py-1 rounded text-[0.65rem] ot-heading tracking-widest text-blue-400 border border-blue-400/30 hover:bg-blue-400/10 transition-colors shrink-0"
                        >
                          MODIFICA
                        </button>
                        <button onClick={() => removeItem(i)} data-testid={`delete-import-${i.id}`} className="p-1.5 rounded text-red-400 hover:bg-red-500/10 transition-colors shrink-0">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="ot-panel-elev p-4 ot-fade-up" style={{ animationDelay: "80ms" }}>
            <div className="ot-panel p-4">
              <div className="ot-label mb-1 text-center">TOTALE IMPORT</div>
              <div className="ot-mono text-4xl font-black text-center text-blue-400" style={{ textShadow: "0 0 24px rgba(47,107,255,0.45)" }} data-testid="import-total">
                $ {fmt(total)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                data-testid="import-reset-btn"
                onClick={() => setQty(Object.fromEntries(items.map((i) => [i.id, 0])))}
                className="ot-btn-ghost flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Resetta
              </button>
              <button data-testid="import-copy-btn" onClick={copyTotal} disabled={total <= 0} className="ot-btn-ghost flex items-center justify-center gap-2">
                <Copy className="w-3.5 h-3.5" /> Copia Lista
              </button>
            </div>
            {total > 0 && (
              <div className="ot-panel p-3 mt-3">
                <div className="ot-label mb-2">ANTEPRIMA LISTA COPIATA</div>
                <pre className="ot-mono text-[0.7rem] text-slate-300 whitespace-pre-wrap leading-relaxed" data-testid="import-copy-preview">{receiptText}</pre>
              </div>
            )}
          </div>

          <div className="ot-panel-elev p-4 ot-fade-up" style={{ animationDelay: "120ms" }}>
            <h2 className="ot-heading text-lg font-bold text-slate-100 mb-3">Nuova Voce</h2>
            <div className="space-y-3">
              <div>
                <label className="ot-label block mb-1">NOME VOCE</label>
                <input data-testid="new-import-label" className="ot-input" placeholder="Es. Kit Sospensioni" value={newItem.label} onChange={(e) => setNewItem({ ...newItem, label: e.target.value })} />
              </div>
              <div>
                <label className="ot-label block mb-1">PREZZO ($)</label>
                <input data-testid="new-import-price" type="number" className="ot-input ot-mono" placeholder="0" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} />
              </div>
              <button data-testid="add-import-btn" disabled={busy} onClick={addItem} className="ot-btn-primary w-full flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" /> Aggiungi Voce
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
